import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate, API_BASE } from "../lib/api";
import { MODULE_ORDER, MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, FileJson, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [nw, setNw] = useState(null);
  const [pack, setPack] = useState(null);

  useEffect(() => {
    api.get("/reports/net-worth").then((r) => setNw(r.data));
  }, []);

  const generatePack = async () => {
    const { data } = await api.get("/reports/family-pack");
    setPack(data);
    toast.success("Family pack generated");
  };

  const downloadPackJson = () => {
    if (!pack) return;
    const blob = new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "family_emergency_pack.json"; a.click();
    URL.revokeObjectURL(url);
  };

  const printPack = () => {
    if (!pack) { generatePack(); return; }
    window.print();
  };

  const exportCsv = async (module) => {
    try {
      const resp = await api.get(`/reports/csv/${module}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${module}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="label-overline">Reports</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
          Reports & Exports
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Generate a complete, printable Emergency Family Pack or export per-module CSVs.
        </p>
      </div>

      {/* Net worth */}
      {nw && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="label-overline">Assets</div>
            <div className="stat-value mt-2">{formatCurrency(nw.total_assets, true)}</div>
            <div className="text-xs text-slate-500 mt-1">{nw.assets.length} items</div>
          </Card>
          <Card className="p-5">
            <div className="label-overline">Liabilities</div>
            <div className="stat-value mt-2">{formatCurrency(nw.total_liabilities, true)}</div>
            <div className="text-xs text-slate-500 mt-1">{nw.liabilities.length} items</div>
          </Card>
          <Card className="p-5 bg-slate-900 text-white">
            <div className="label-overline text-slate-300">Net Worth</div>
            <div className="stat-value mt-2">{formatCurrency(nw.total_assets - nw.total_liabilities, true)}</div>
          </Card>
        </div>
      )}

      <Card className="p-6" data-testid="reports-family-pack-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-heading font-semibold text-xl">Emergency Family Pack</div>
            <div className="text-sm text-slate-600 mt-1 max-w-xl">
              A single exportable snapshot of all your modules, action plan, emergency contacts
              and key instructions — for your family when it's needed most.
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={generatePack} data-testid="reports-generate-pack-button">
              <FileText className="h-4 w-4 mr-2" /> Generate / Refresh
            </Button>
            <Button onClick={downloadPackJson} disabled={!pack} data-testid="reports-download-json-button">
              <FileJson className="h-4 w-4 mr-2" /> Download JSON
            </Button>
            <Button variant="outline" onClick={printPack} data-testid="reports-print-button">
              <Download className="h-4 w-4 mr-2" /> Print / PDF
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="font-heading font-semibold mb-3">Per-module CSV Exports</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {MODULE_ORDER.map((slug) => (
            <Button key={slug} variant="outline" size="sm" className="justify-start"
              onClick={() => exportCsv(slug)}
              data-testid={`reports-csv-${slug}`}>
              <Download className="h-4 w-4 mr-2" /> {MODULE_SCHEMAS[slug].label}
            </Button>
          ))}
        </div>
      </Card>

      {pack && (
        <Card className="p-6 print:shadow-none" id="family-pack-printable">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="label-overline">Family Emergency Pack</div>
              <div className="font-heading text-2xl font-semibold mt-1">{pack.owner?.full_name}</div>
              <div className="text-xs text-slate-500">Generated {formatDate(pack.generated_at)}</div>
            </div>
          </div>
          {pack.action_plan?.family_message && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-6">
              <div className="label-overline mb-2">A message to my family</div>
              <p className="text-sm whitespace-pre-wrap">{pack.action_plan.family_message}</p>
            </div>
          )}
          {Object.entries(pack.modules || {}).map(([mod, items]) => (
            <div key={mod} className="mb-6 break-inside-avoid">
              <div className="font-heading font-semibold border-b border-slate-200 pb-2 mb-3">
                {MODULE_SCHEMAS[mod]?.label || mod}
                <span className="ml-2 text-xs text-slate-500">({items.length})</span>
              </div>
              <div className="space-y-3">
                {items.map((i, idx) => (
                  <div key={idx} className="text-sm">
                    <div className="font-medium">{i.title}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mt-1">
                      {Object.entries(i.data || {}).map(([k, v]) =>
                        v ? (
                          <div key={k}>
                            <span className="text-slate-500">{k}:</span>{" "}
                            <span className="tabular-nums">{String(v)}</span>
                          </div>
                        ) : null
                      )}
                    </div>
                    {i.remarks && <div className="text-xs text-slate-500 italic mt-1">{i.remarks}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
